<template>
  <div class="m-0" v-if="isEarthMoonTrajectoryMission">
    <div
      v-if="!displayResult"
      class="d-inline-flex justify-center align-center"
    >
      <span class="headline grey--text">{{
        currentMission.name.toUpperCase()
      }}</span>
    </div>
    <v-spacer></v-spacer>
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation> </exo-mission-computation>
      <div class="font-weight-light subheading align-center">
        The computation process of an Earth-Moon trajectory might take several
        minutes, and can reach up to around 90 minutes.
      </div>
    </div>

    <div v-if="displayResult">
      <v-row>
        <v-container class="pb-3">
          <v-row no-gutters>
            <v-col>
              <div class="d-inline-flex justify-center align-center">
                <span class="headline grey--text">{{
                  currentMission.name.toUpperCase()
                }}</span>
                <exo-help-dialog
                  icon-scale="1.5"
                  title="Earth-Moon Trajectory Results"
                >
                <p>
                  In addition to the global results (such as ΔV, used propellant, thrust direction and mission duration), a set of ephemerides throughout the mission are also made available, and can be acessible either by exporting the data or through the plots shown.
                </p>
                <p>
                  The available ephemerides are:
                  <ul>
                    <li><b>Position</b>: spacecraft position (x,y,z) throught the trajectory, in the synodic reference frame (rotating frame centered at the barycenter of the 
Earth and the Moon, with x-axis along the Earth-Moon direction). The values are normalized by the Earth-Moon distance; </li>
                    <li><b>Mass</b>: instantaneous spacecraft mass. It is only consumed when the propulsion system is active, and at a constant rate (corresponding to the constant thrust level);</li>
                    <li><b>Control angles</b>: azimuth (alpha) and elevation (delta) of the thrust direction vector in the local frame. The local frame is a (t, n, w) local satellite frame defined from the spacecraft coordinates expressed in the rotating frame (centered at the Earth for the escape phase and at the Moon for the capture phase). When the propulsion system is not active, alpha and delta are arbitrarily set to 0 and undefined, respectively.</li>
                  </ul>
                </p>
                </exo-help-dialog>
              </div>
            </v-col>
            <v-menu
              transition="slide-y-transition"
              nudge-bottom="15"
              offset-y
              bottom
              min-width="600"
              max-height="600"
              v-if="displayResult"
            >
              <template v-slot:activator="{ on }">
                <v-btn class="mr-10" color="primary" dark v-on="on">
                  Export
                  <v-icon dark>arrow_drop_down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click.stop="exportEphemerides()">
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Position Ephemerides</v-list-item-title
                  >
                </v-list-item>
                <v-list-item @click.stop="exportControls()">
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Control data</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-menu>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="saveLayout()"
                >
                  <icon scale="1.5" class="primary--text" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="dashboardTools().resetLayout(_self)"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="undo-alt"
                  ></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
          </v-row>
        </v-container>
      </v-row>
      <v-divider class="mt-0"></v-divider>
      <div class="px-3">
        <grid-layout
          :layout.sync="gridlayout"
          :col-num="12"
          :row-height="7"
          :is-draggable="true"
          :is-resizable="true"
          :is-mirrored="false"
          :vertical-compact="true"
          :margin="[20, 10]"
          :use-css-transforms="true"
        >
          <grid-item
            :x="gridlayout[0].x"
            :y="gridlayout[0].y"
            :w="gridlayout[0].w"
            :h="gridlayout[0].h"
            :i="gridlayout[0].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#2A94DE"
              awesomeicon="tachometer-alt"
              title="ΔV"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['deltaV'])"
              :unit="getResultUnitLabel('deltaV')"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[1].x"
            :y="gridlayout[1].y"
            :w="gridlayout[1].w"
            :h="gridlayout[1].h"
            :i="gridlayout[1].i"
            :minH="5"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#FF7E0D"
              icon="ev_station"
              title="Used propellant"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="computeResultToDisplay(['usedPropellant'])"
              :unit="getResultUnitLabel('usedPropellant')"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[2].x"
            :y="gridlayout[2].y"
            :w="gridlayout[2].w"
            :h="gridlayout[2].h"
            :i="gridlayout[2].i"
            :minH="5"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#38AD38"
              icon="timelapse"
              title="Thrust duration"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="computeResultToDisplay(['thrustDuration'])"
              :unit="getResultUnitLabel('thrustDuration')"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[3].x"
            :y="gridlayout[3].y"
            :w="gridlayout[3].w"
            :h="gridlayout[3].h"
            :i="gridlayout[3].i"
            :minH="5"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#F72C2C"
              icon="timer"
              title="Mission duration"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="computeResultToDisplay(['missionDuration'])"
              :unit="getResultUnitLabel('missionDuration')"
            />
          </grid-item>

          <grid-item
            :x="gridlayout[4].x"
            :y="gridlayout[4].y"
            :w="gridlayout[4].w"
            :h="gridlayout[4].h"
            :i="gridlayout[4].i"
            :minH="5"
            drag-ignore-from=".no-drag"
          >
            <v-row style="width: 100%; justify-content: center"
              ><div
                id="plotDivTrajectory"
                style="width: 100%; height: 100%"
                class="no-drag"
              ></div>
            </v-row>
            <v-row>
            </v-row>
          </grid-item>
          <grid-item
            :x="gridlayout[5].x"
            :y="gridlayout[5].y"
            :w="gridlayout[5].w"
            :h="gridlayout[5].h"
            :i="gridlayout[5].i"
            :minH="5"
            :isDraggable="true"
          >
            <v-row style="width: 100%; justify-content: center"
              ><div id="plotDivMass" style="width: 100%; height: 100%"></div>
            </v-row>
          </grid-item>
          <grid-item
            :x="gridlayout[6].x"
            :y="gridlayout[6].y"
            :w="gridlayout[6].w"
            :h="gridlayout[6].h"
            :i="gridlayout[6].i"
            :minH="5"
            :isDraggable="true"
          >
            <v-row style="width: 100%; justify-content: center"
              ><div id="plotDivAlpha" style="width: 100%; height: 100%"></div>
            </v-row>
          </grid-item>
          <grid-item
            :x="gridlayout[7].x"
            :y="gridlayout[7].y"
            :w="gridlayout[7].w"
            :h="gridlayout[7].h"
            :i="gridlayout[7].i"
            :minH="5"
            :isDraggable="true"
          >
            <v-row style="width: 100%; justify-content: center"
              ><div id="plotDivDelta" style="width: 100%; height: 100%"></div>
            </v-row>
          </grid-item>
        </grid-layout>
      </div>
    </div>
  </div>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import Plotly from 'plotly.js-dist'
import errorMixin from '../../mixins/logHandler.js'
import { setTimeout } from 'timers'

Vue.use(dashboardComponents)

export default {
  name: 'EarthMoonTrajectoryDashboard',
  mixins: [errorMixin, plotLayout, plotInput, plotData],
  data() {
    const self = this
    return {
      configurations: [],
      isDrawn: [],
      configuration: null,
      type: 'EarthMoonTrajectoryResult',
      plotingAreaName: 'plotingAreaAnalytical',
      statsCardName: 'statsCard',
      defaultLayout: [
        { x: 0, y: 0, w: 2, h: 6, i: '0' },
        { x: 2, y: 0, w: 2, h: 6, i: '1' },
        { x: 4, y: 0, w: 2, h: 6, i: '2' },
        { x: 6, y: 0, w: 2, h: 6, i: '3' },
        { x: 0, y: 1, w: 8, h: 28, i: '4' },
        { x: 8, y: 0, w: 4, h: 28, i: '5' },
        { x: 0, y: 2, w: 4, h: 28, i: '6' },
        { x: 4, y: 3, w: 4, h: 28, i: '7' }
      ],
      gridlayout: [
        { x: 0, y: 0, w: 2, h: 6, i: '0' },
        { x: 2, y: 0, w: 2, h: 6, i: '1' },
        { x: 4, y: 0, w: 2, h: 6, i: '2' },
        { x: 6, y: 0, w: 2, h: 6, i: '3' },
        { x: 0, y: 1, w: 8, h: 28, i: '4' },
        { x: 8, y: 0, w: 4, h: 28, i: '5' },
        { x: 0, y: 2, w: 4, h: 28, i: '6' },
        { x: 4, y: 3, w: 4, h: 28, i: '7' }
      ],

      plotConfig: { responsive: true }
    }
  },

  created() {
    window.addEventListener('beforeunload', this.saveLayout)
  },

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)

    setTimeout(() => {
      this.loadLayout()
    }, 1000)
  },

  beforeDestroy() {
    this.cleanPlots()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
  },

  computed: {
    ...mapGetters(['currentUser', 'currentMission', 'currentResultsLog']),
    displayComputationWidget() {
      return this.computationNeeded || !this.resultsLoaded
    },
    displayResult() {
      return this.resultsLoaded && !this.computationNeeded
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },
    resultsLoaded() {
      return (
        this.currentResultsLog != null && this.currentResultsLog.results != null
      )
    },
    isEarthMoonTrajectoryMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'EarthMoonTrajectoryRequest'
      )
    },
    resultAvailable() {
      return (
        this.isEarthMoonTrajectoryMission &&
        this.resultsLoaded &&
        Array.isArray(this.currentResultsLog.results)
      )
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      this.loadLayout()
      setTimeout(() => {
        this.resizeAllStatsCardResized()
      }, 200)
    },
    addPlots() {
      setTimeout(() => {
        const plotDivTrajectory = document.getElementById('plotDivTrajectory')
        const plotDivMass = document.getElementById('plotDivMass')
        const plotDivAlpha = document.getElementById('plotDivAlpha')
        const plotDivDelta = document.getElementById('plotDivDelta')
        if (
          this.resultsLoaded &&
          plotDivTrajectory != null &&
          plotDivMass != null &&
          plotDivAlpha != null &&
          plotDivDelta != null
        ) {
          let ephemeridesData = this.currentResultsLog.results.ephemerides
          let controlsData = this.currentResultsLog.results.controls
          let plotLayout = {
            autosize: true,
            title: 'Trajectory in the synodic reference frame (CR3BP)',

            scene: {
              aspectmode: 'data',

              xaxis: {
                title: { text: 'x' }
              },
              yaxis: {
                title: { text: 'y' }
              },
              zaxis: {
                title: { text: 'z' }
              },

              camera: {
                center: {
                  x: 0.6,
                  y: 0,
                  z: -0.05
                },
                eye: {
                  x: 2.2,
                  y: 2.2,
                  z: 2.2
                }
              }
            }
          }
          Plotly.newPlot(
            plotDivTrajectory,
            [
              {
                x: ephemeridesData.map((x) => x[1]),
                y: ephemeridesData.map((x) => x[2]),
                z: ephemeridesData.map((x) => x[3]),
                type: 'scatter3d',
                marker: {
                  size: 1,
                  color: 'black',
                  line: {
                    color: 'black',
                    width: 1
                  }
                },
                name: 'Trajectory'
              },
              {
                x: [-0.012],
                y: [0.0],
                z: [0.0],
                mode: 'markers',
                marker: {
                  color: 'blue',
                  size: 12,
                  symbol: 'circle',
                  line: {
                    color: 'blue',
                    width: 1
                  },
                  opacity: 1.0
                },
                type: 'scatter3d',
                name: 'Earth'
              },
              {
                x: [0.988],
                y: [0.0],
                z: [0.0],
                mode: 'markers',
                marker: {
                  color: 'grey',
                  size: 6,
                  symbol: 'circle',
                  line: {
                    color: 'grey',
                    width: 1
                  },
                  opacity: 1.0
                },
                type: 'scatter3d',
                name: 'Moon'
              }
            ],
            plotLayout,
            this.plotConfig
          )

          let plotLayoutMass = {
            autosize: true,
            title: 'Mass consumption over time',
            xaxis: {
              title: { text: 't(days)' }
            },
            yaxis: {
              title: { text: 'm(kg)' }
            }
          }

          Plotly.newPlot(
            plotDivMass,
            [
              {
                x: ephemeridesData.map((x) => x[0] / 86400.0),
                y: ephemeridesData.map((x) => x[4]),
                marker: {
                  size: 2,
                  color: 'black',
                  line: {
                    color: 'black',
                    width: 1
                  }
                },
                type: 'scatter'
              }
            ],
            plotLayoutMass,
            this.plotConfig
          )

          let plotLayoutAlpha = {
            title: 'Alpha (maneuver direction vector) over time',
            xaxis: {
              title: { text: 't(days)' }
            },
            yaxis: {
              title: { text: 'alpha(rad)' }
            }
          }

          Plotly.newPlot(
            plotDivAlpha,
            [
              {
                x: controlsData.map((x) => x[0] / 86400.0),
                y: controlsData.map((x) => x[1]),
                marker: {
                  size: 2,
                  color: 'black',
                  line: {
                    color: 'black',
                    width: 1
                  }
                },
                type: 'scatter'
              }
            ],
            plotLayoutAlpha,
            this.plotConfig
          )

          let plotLayoutDelta = {
            title: 'Delta (maneuver direction vector) over time',
            xaxis: {
              title: { text: 't(days)' }
            },
            yaxis: {
              title: { text: 'delta(rad)' }
            }
          }

          Plotly.newPlot(
            plotDivDelta,
            [
              {
                x: controlsData.map((x) => x[0] / 86400.0),
                y: controlsData.map((x) => x[2]),
                marker: {
                  size: 2,
                  color: 'black',
                  line: {
                    color: 'black',
                    width: 1
                  }
                },
                type: 'scatter'
              }
            ],
            plotLayoutDelta,
            this.plotConfig
          )
        }
      }, 100)
    },
    cleanPlots() {
      try {
        Plotly.purge('plotDivTrajectory')
      } catch (e) {}
      try {
        Plotly.purge('plotDivMass')
      } catch (e) {}
      try {
        Plotly.purge('plotDivAlpha')
      } catch (e) {}
      try {
        Plotly.purge('plotDivDelta')
      } catch (e) {}
    },
    dashboardTools() {
      return dashboardTools
    },
    saveLayout() {
      dashboardTools.saveLayout(this)
    },
    loadLayout() {
      if (this.currentMission) {
        if (this.currentMission.layout) {
          let missionLayout = JSON.parse(this.currentMission.layout)
          dataTools.loadGridLayoutConfig(missionLayout, this)
        } else {
          let missionLayout = { configurations: [], layout: this.defaultLayout }
          dataTools.loadGridLayoutConfig(missionLayout, this)
        }
      }
      dashboardTools.removeDuplicatedGridItem(this)
      this.addPlots()
    },
    resizeAllStatsCardResized() {
      this.statsCardResized(0)
      this.statsCardResized(1)
      this.statsCardResized(2)
      this.statsCardResized(3)
    },

    statsCardResized(i) {
      return dashboardTools.statsCardResized(this, i)
    },

    plotCardResized(i, newH, newW, newHPx, newWPx) {
      let update = {
        width: newWPx,
        height: newHPx - 50 // TODO find a better way to get this value
      }
      let plotingArea = document.getElementById(`${this.plotingAreaName}${i}`)
      if (plotingArea != null) {
        try {
          Plotly.relayout(plotingArea, update)
        } catch (e) {
          console.log('Error calling Plotly.relayout')
        }
      }
    },

    exportEphemerides() {
      let dataToExport = ''
      dataToExport +=
        'Simulation duration' +
        ',' +
        'X coordinate' +
        ',' +
        'Y coordinate' +
        ',' +
        'Z coordinate' +
        ',' +
        'Spacecraft mass' +
        '\n'
      this.currentResultsLog.results.ephemerides.forEach((entry) => {
        for (let index = 0; index < entry.length; index++) {
          dataToExport += entry[index] + ','
        }
        dataToExport += '\n'
      })
      dataTools.downloadFile(
        this.currentMission.name + '_earth_moon_trajectory_ephemerides',
        dataToExport
      )
    },

    exportControls() {
      let dataToExport = ''
      dataToExport +=
        'Simulation duration' + ',' + 'Alpha' + ',' + 'Beta' + '\n'
      this.currentResultsLog.results.controls.forEach((entry) => {
        for (let index = 0; index < entry.length; index++) {
          dataToExport += entry[index] + ','
        }
        dataToExport += '\n'
      })
      dataTools.downloadFile(
        this.currentMission.name + '_earth_moon_trajectory_controls',
        dataToExport
      )
    },

    getResultFieldType(field) {
      return resultsDescription.EarthMoonTrajectoryResult[field].type
    },

    getResultUnitLabel(field) {
      let fieldType = this.getResultFieldType(field)
      return (
        ' ' + dataTools.getUnitDisplayed(dataTools.getDefaultGuiUnit(fieldType))
      )
    },

    computeResultToDisplay(fieldPath) {
      let fieldType = this.getResultFieldType(fieldPath[fieldPath.length - 1])
      if (
        this.resultAvailable != null &&
        this.currentResultsLog.results != null
      ) {
        return dataTools.convertSi2Display(
          fieldType,
          dataTools.getValue(this.currentResultsLog.results, fieldPath)
        )
      } else {
        return ''
      }
    },

    getCurrentAPI() {
      if (this.currentMission) {
        return this.getAPI(this.currentMission.typeName + 's')
      }
    },

    getAPI(objectAPI) {
      return '/users/' + this.currentUser.id + '/' + objectAPI.toLowerCase()
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
